import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lightning } from '@phosphor-icons/react';
import { useT } from '../i18n/I18nContext';

export default function AuthPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t('auth.signedInToast'));
      nav('/', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('auth.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="auth-page">
      {/* Left visual side */}
      <div className="hidden md:flex flex-col justify-between w-1/2 bg-zinc-950 text-white p-12 relative overflow-hidden">
        <div className="flex items-center gap-2 z-10">
          <div className="w-7 h-7 bg-[#0055ff] flex items-center justify-center">
            <Lightning size={16} weight="fill" />
          </div>
          <span className="font-display font-black text-lg tracking-tight">CMR</span>
        </div>
        <div className="z-10">
          <h1 className="font-display font-black text-5xl leading-[0.95] tracking-tight">
            {t('auth.heroLine1')}<br/>
            <span className="text-[#0055ff]">{t('auth.heroLine2')}</span>
          </h1>
          <p className="mt-6 text-zinc-400 text-base max-w-md leading-relaxed">
            {t('auth.heroSubtitle')}
          </p>
          <div className="mt-10 grid grid-cols-2 gap-px bg-zinc-800 max-w-md">
            {[t('auth.features.dnd'), t('auth.features.multiUser'), t('auth.features.claude'), t('auth.features.gitlab')].map((f) => (
              <div key={f} className="bg-zinc-950 p-4 text-xs font-mono text-zinc-400 uppercase tracking-wider">
                {f}
              </div>
            ))}
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 z-10 font-mono">
          {t('auth.footer')}
        </div>
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'linear-gradient(#222 1px, transparent 1px), linear-gradient(90deg, #222 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />
      </div>

      {/* Right form side */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="md:hidden flex items-center gap-2 mb-8">
            <div className="w-7 h-7 bg-black text-white flex items-center justify-center">
              <Lightning size={16} weight="fill" />
            </div>
            <span className="font-display font-black tracking-tight">CMR</span>
          </div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-500 mb-2">
            {t('auth.badge')}
          </p>
          <h2 className="font-display text-3xl font-black tracking-tight mb-8">
            {t('auth.welcomeBack')}
          </h2>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider font-semibold">{t('auth.emailLabel')}</Label>
              <Input
                id="email"
                data-testid="auth-email-input"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-sm border-zinc-300 mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider font-semibold">{t('auth.passwordLabel')}</Label>
              <Input
                id="password"
                data-testid="auth-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-sm border-zinc-300 mt-1"
              />
            </div>
            <Button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={loading}
              className="w-full rounded-sm bg-black text-white hover:bg-zinc-800 h-10"
            >
              {loading ? t('auth.signingIn') : t('auth.signIn')}
            </Button>
          </form>
          <p className="text-sm text-zinc-500 mt-6">
            {t('auth.loginHint')}
          </p>
        </div>
      </div>
    </div>
  );
}
